import {
  FiberManualRecord as FiberManualRecordIcon,
  SettingsOutlined,
} from '@mui/icons-material';
import { Chip, Divider, ListItemIcon, Menu, MenuItem } from '@mui/material';
import { useState } from 'react';

import { VenuePreset } from '@/domain/project-venue-presets/entities/project-venue-presets.entity';
import { selectProjectVenuesPresetsItems } from '@/domain/project-venue-presets/project-venue-presets.selectors';
import { useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';
import { truncateWithEllipsis } from '@/ui/utils/truncate-text';

export function PresetTag({
  presetId,
  onClickPresets,
  onSelectPreset,
  disabled,
}: {
  presetId: number | undefined;
  onClickPresets: () => void;
  onSelectPreset: (presetId: number) => void;
  disabled?: boolean;
}) {
  const presets = useAppSelector(selectProjectVenuesPresetsItems);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [hovered, setHovered] = useState(false);

  const preset = presets.find((p) => p.id === presetId);
  if (!preset) return null;

  const label = truncateWithEllipsis(preset.presetTitle || '', 20);
  const color = preset.presetColor;

  const onClose = () => setAnchorEl(null);

  const onSelect = (selectedPresetId: number) => {
    onSelectPreset(selectedPresetId);
    onClose();
  };

  const isExpanded = hovered || Boolean(anchorEl);
  const chipLabel = getChipLabel(label, isExpanded);
  const chipMaxWidth = getChipMaxWidth(isExpanded);

  return (
    <>
      <Chip
        label={chipLabel}
        size="small"
        disabled={disabled}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        sx={{
          alignSelf: 'center',
          bgcolor: color,
          fontWeight: 600,
          fontSize: '1rem',
          color: 'black',
          maxWidth: chipMaxWidth,
          transition: 'max-width 0.5s ease',
          overflow: 'hidden',
          '&:hover': { bgcolor: color },
        }}
      />
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={onClose}>
        {presets.map((p) => (
          <PresetMenuItem
            key={p.id}
            preset={p}
            selected={p.id === presetId}
            onClick={() => onSelect(p.id || NaN)}
          />
        ))}
        <Divider />
        <ConfiguratePresetMenuItem onClickPresets={onClickPresets} />
      </Menu>
    </>
  );
}

function getChipLabel(label: string, isExpanded: boolean): string {
  if (isExpanded) {
    return label;
  }
  return getFirstGrapheme(label);
}

function getFirstGrapheme(text: string): string {
  return [...new Intl.Segmenter().segment(text)][0]?.segment ?? '';
}

function getChipMaxWidth(isExpanded: boolean): string {
  return isExpanded ? '20rem' : '3rem';
}

function PresetMenuItem({
  preset,
  selected,
  onClick,
}: {
  preset: VenuePreset;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <MenuItem key={preset.id} onClick={onClick} selected={selected}>
      <ListItemIcon sx={{ minWidth: 28 }}>
        <FiberManualRecordIcon
          sx={{ color: preset.presetColor || '', fontSize: 16 }}
        />
      </ListItemIcon>
      {preset.presetTitle}
    </MenuItem>
  );
}

function ConfiguratePresetMenuItem({
  onClickPresets,
}: {
  onClickPresets: () => void;
}) {
  return (
    <MenuItem onClick={onClickPresets}>
      <ListItemIcon sx={{ minWidth: 28 }}>
        <SettingsOutlined sx={{ fontSize: 16 }} />
      </ListItemIcon>
      {translate('VenueDates.QuizPanel.change-presets')}
    </MenuItem>
  );
}
